/**
 * Health Check Quiz Session Storage
 * Manages localStorage for quiz state persistence across page refreshes
 * Sessions are stored permanently until explicitly cleared
 */

const STORAGE_PREFIX = "healthcheck_session_";

/**
 * Generate storage key for a user's quiz session
 */
const getStorageKey = (userId, bookTypeId) => {
  return `${STORAGE_PREFIX}${userId}_${bookTypeId}`;
};

/**
 * Save quiz session to localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @param {object} sessionData - Quiz session data
 */
export const saveHealthCheckSession = (userId, bookTypeId, sessionData) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    const data = {
      ...sessionData,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(data));
    console.log("[HealthCheck Storage] Session saved:", key);
  } catch (error) {
    console.error("[HealthCheck Storage] Error saving session:", error);
  }
};

/**
 * Load quiz session from localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @returns {object|null} - Session data or null if not found
 */
export const loadHealthCheckSession = (userId, bookTypeId) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      console.log("[HealthCheck Storage] No session found:", key);
      return null;
    }

    const data = JSON.parse(stored);
    console.log("[HealthCheck Storage] Session loaded:", key);
    return data;
  } catch (error) {
    console.error("[HealthCheck Storage] Error loading session:", error);
    return null;
  }
};

/**
 * Clear quiz session from localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 */
export const clearHealthCheckSession = (userId, bookTypeId) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    localStorage.removeItem(key);
    console.log("[HealthCheck Storage] Session cleared:", key);
  } catch (error) {
    console.error("[HealthCheck Storage] Error clearing session:", error);
  }
};

/**
 * Check if a quiz session exists
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @returns {boolean}
 */
export const hasHealthCheckSession = (userId, bookTypeId) => {
  const session = loadHealthCheckSession(userId, bookTypeId);
  return session !== null;
};

/**
 * Get all active health check sessions for a user
 * @param {string} userId - User ID
 * @returns {Array} - Array of session data objects
 */
export const getAllUserSessions = (userId) => {
  try {
    const sessions = [];
    const prefix = `${STORAGE_PREFIX}${userId}_`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const data = JSON.parse(stored);
          sessions.push({
            key,
            bookTypeId: key.replace(prefix, ""),
            ...data
          });
        }
      }
    }

    return sessions;
  } catch (error) {
    console.error("[HealthCheck Storage] Error getting all sessions:", error);
    return [];
  }
};

/**
 * Clear all health check sessions for a user
 * @param {string} userId - User ID
 */
export const clearAllUserSessions = (userId) => {
  try {
    const prefix = `${STORAGE_PREFIX}${userId}_`;
    const keysToRemove = [];

    // Collect all keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    // Remove all collected keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      console.log("[HealthCheck Storage] Session cleared:", key);
    });

    if (keysToRemove.length > 0) {
      console.log(`[HealthCheck Storage] Cleared ${keysToRemove.length} session(s) for user ${userId}`);
    }
  } catch (error) {
    console.error("[HealthCheck Storage] Error clearing all sessions:", error);
  }
};
